import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSidenav } from '@angular/material/sidenav';
import { Router } from '@angular/router';
import { BlockUI, NgBlockUI } from 'ng-block-ui';
import { ICommunication } from '../../../models/communication.model';
import { CommunicationsService } from '../../../services/communications.service';
import { SidenavService } from '../../../services/sidenav.service';
import { SnackbarService } from '../../../services/snackbar.service';
import { ConfirmModalComponent } from '../../shared/confirm-modal/confirm-modal.component';

@Component({
  selector: 'app-communications-list',
  templateUrl: './communications-list.component.html',
  styleUrls: ['./communications-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CommunicationsListComponent implements OnInit {
  @BlockUI() blockUI!: NgBlockUI;

  sidenav: MatSidenav;
  loading = false;
  communications: ICommunication[] = [];

  constructor(
    private sidenavService: SidenavService,
    private communicationsService: CommunicationsService,
    private snackbar: SnackbarService,
    private dialog: MatDialog,
    private router: Router,
    private cdr: ChangeDetectorRef,
  ) {
    this.sidenav = this.sidenavService.getSidenav();
  }

  async ngOnInit(): Promise<void> {
    await this.loadList();
  }

  toggleMenu(): void {
    this.sidenavService.toggle();
  }

  goBack(): void {
    this.router.navigate(['/admin']);
  }

  newCommunication(): void {
    this.router.navigate(['/admin/communications/new']);
  }

  viewDetail(id: string): void {
    this.router.navigate(['/admin/communications', id]);
  }

  editCommunication(id: string, event: Event): void {
    event.stopPropagation();
    this.router.navigate(['/admin/communications', id], {
      queryParams: { mode: 'edit' },
    });
  }

  async sendCommunication(id: string, event: Event): Promise<void> {
    event.stopPropagation();
    this.blockUI.start();
    try {
      await this.communicationsService.send(id);
      this.snackbar.success('Comunicado enviado correctamente.');
      await this.refreshList();
    } catch {
      this.snackbar.error('Error al enviar el comunicado.');
    } finally {
      this.blockUI.stop();
    }
  }

  async sendWhatsAppCommunication(id: string, event: Event): Promise<void> {
    event.stopPropagation();
    this.dialog
      .open(ConfirmModalComponent, {
        data: {
          title: 'Enviar por WhatsApp',
          message:
            'Se enviará este comunicado por WhatsApp a todos los participantes con teléfono guardado.',
          cancelText: 'Cancelar',
          confirmText: 'Enviar',
          icon: 'chat',
          tone: 'highlight',
        },
      })
      .afterClosed()
      .subscribe(async (result) => {
        if (!result) {
          return;
        }

        this.blockUI.start();
        try {
          const summary = await this.communicationsService.sendWhatsApp(id);
          if (summary.errorCount > 0) {
            this.snackbar.error(
              `WhatsApp enviado a ${summary.sentCount}/${summary.totalRecipients}. Errores: ${summary.errorCount}.`,
            );
          } else {
            this.snackbar.success(
              `WhatsApp enviado a ${summary.sentCount} participantes.`,
            );
          }
        } catch {
          this.snackbar.error('Error al enviar el comunicado por WhatsApp.');
        } finally {
          this.blockUI.stop();
          this.cdr.markForCheck();
        }
      });
  }

  async deleteCommunication(id: string, event: Event): Promise<void> {
    event.stopPropagation();
    this.dialog
      .open(ConfirmModalComponent, {
        data: {
          title: 'Eliminar comunicado',
          message: '¿Estás seguro de que quieres eliminar este comunicado?',
          cancelText: 'Cancelar',
          confirmText: 'Eliminar',
        },
      })
      .afterClosed()
      .subscribe(async (result) => {
        if (!result) {
          return;
        }

        this.blockUI.start();
        try {
          await this.communicationsService.delete(id);
          this.snackbar.success('Comunicado eliminado correctamente.');
          await this.refreshList();
        } catch {
          this.snackbar.error('Error al eliminar el comunicado.');
        } finally {
          this.blockUI.stop();
        }
      });
  }

  private async loadList(): Promise<void> {
    this.loading = true;
    this.blockUI.start();
    try {
      await this.refreshList();
    } finally {
      this.loading = false;
      this.blockUI.stop();
      this.cdr.markForCheck();
    }
  }

  private async refreshList(): Promise<void> {
    this.communications = await this.communicationsService.getList();
    this.cdr.markForCheck();
  }
}
